import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { 
  playerProfileService, 
  clubCareerService, 
  performanceStatsService, 
  transferHistoryService, 
  nationalTeamSeniorService,
  nationalTeamYouthService,
  medicalRecordsService,
  advancedPerformanceService,
  competitionsPlayedService,
  marketValueHistoryService,
  playerMediaService
} from '../../../services/playerMasterService';
import { playerMediaStorage } from '../../../services/storageService';





const PlayerEditModal = ({ player, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [careerHistory, setCareerHistory] = useState([]);
  const [performanceStats, setPerformanceStats] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [nationalTeamSenior, setNationalTeamSenior] = useState(null);
  const [nationalTeamYouth, setNationalTeamYouth] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [advancedPerformance, setAdvancedPerformance] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [marketValueHistory, setMarketValueHistory] = useState([]);
  const [media, setMedia] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentPlayerId, setCurrentPlayerId] = useState(player?.id || null);
  const [playerJustCreated, setPlayerJustCreated] = useState(false);
  
  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  const [formData, setFormData] = useState({
    fullName: '',
    knownAs: '',
    dateOfBirth: '',
    placeOfBirth: '',
    nationalityPrimary: '',
    nationalitySecondary: '',
    height: '',
    weight: '',
    preferredFoot: 'right',
    primaryPosition: 'striker',
    currentClub: '',
    currentLeague: '',
    currentCountry: '',
    squadNumber: '',
    contractType: 'permanent',
    contractStartDate: '',
    contractEndDate: '',
    availableForTransfer: false,
    availableForLoan: false,
    currentMarketValue: '',
    highestMarketValue: '',
    agentName: '',
    agencyName: '',
    agentEmail: '',
    agentPhone: '',
    verifiedStatus: false,
    fifaVerified: false,
    profileVisibility: 'public',
    profilePhoto: ''
  });

  useEffect(() => {
    if (player) {
      setCurrentPlayerId(player?.id);
      setFormData({
        fullName: player?.fullName || '',
        knownAs: player?.knownAs || '',
        dateOfBirth: player?.dateOfBirth || '',
        placeOfBirth: player?.placeOfBirth || '',
        nationalityPrimary: player?.nationalityPrimary || '',
        nationalitySecondary: player?.nationalitySecondary || '',
        height: player?.height || '',
        weight: player?.weight || '',
        preferredFoot: player?.preferredFoot || 'right',
        primaryPosition: player?.primaryPosition || 'striker',
        currentClub: player?.currentClub || '',
        currentLeague: player?.currentLeague || '',
        currentCountry: player?.currentCountry || '',
        squadNumber: player?.squadNumber || '',
        contractType: player?.contractType || 'permanent',
        contractStartDate: player?.contractStartDate || '',
        contractEndDate: player?.contractEndDate || '',
        availableForTransfer: player?.availableForTransfer || false,
        availableForLoan: player?.availableForLoan || false,
        currentMarketValue: player?.currentMarketValue || '',
        highestMarketValue: player?.highestMarketValue || '',
        agentName: player?.agentName || '',
        agencyName: player?.agencyName || '',
        agentEmail: player?.agentEmail || '',
        agentPhone: player?.agentPhone || '',
        verifiedStatus: player?.verifiedStatus || false,
        fifaVerified: player?.fifaVerified || false,
        profileVisibility: player?.profileVisibility || 'public',
        profilePhoto: player?.profilePhoto || ''
      });
      loadPlayerData();
      loadExistingPhoto();
    }
  }, [player]);

  const loadExistingPhoto = async () => {
    if (player?.profilePhoto) {
      setUploadingPhoto(true);
      const result = await playerMediaStorage?.getSignedUrl(player?.profilePhoto, 3600);
      if (result?.data?.signedUrl) {
        setPhotoPreview(result?.data?.signedUrl);
      }
      setUploadingPhoto(false);
    }
  };

  const loadPlayerData = async () => {
    if (!currentPlayerId) return;
    try {
      const [careerRes, statsRes, transferRes, nationalSeniorRes, nationalYouthRes, medicalRes, advancedRes, competitionsRes, marketValueRes, mediaRes] = await Promise.all([
        clubCareerService?.getByPlayerId(currentPlayerId),
        performanceStatsService?.getByPlayerId(currentPlayerId),
        transferHistoryService?.getByPlayerId(currentPlayerId),
        nationalTeamSeniorService?.getByPlayerId(currentPlayerId),
        nationalTeamYouthService?.getByPlayerId(currentPlayerId),
        medicalRecordsService?.getByPlayerId(currentPlayerId),
        advancedPerformanceService?.getByPlayerId(currentPlayerId),
        competitionsPlayedService?.getByPlayerId(currentPlayerId),
        marketValueHistoryService?.getByPlayerId(currentPlayerId),
        playerMediaService?.getByPlayerId(currentPlayerId)
      ]);
      setCareerHistory(careerRes?.data || []);
      setPerformanceStats(statsRes?.data || []);
      setTransferHistory(transferRes?.data || []);
      setNationalTeamSenior(nationalSeniorRes?.data);
      setNationalTeamYouth(nationalYouthRes?.data || []);
      setMedicalRecords(medicalRes?.data || []);
      setAdvancedPerformance(advancedRes?.data || []);
      setCompetitions(competitionsRes?.data || []);
      setMarketValueHistory(marketValueRes?.data || []);
      setMedia(mediaRes?.data || []);
    } catch (error) {
      console.error('Error loading player data:', error);
    }
  };

  const autoSavePlayerProfile = async () => {
    if (currentPlayerId) {
      return { success: true, playerId: currentPlayerId };
    }

    if (!formData?.fullName || !formData?.dateOfBirth || !formData?.nationalityPrimary) {
      alert('Please fill in required fields (Full Name, Date of Birth, Primary Nationality) in the Basic Info tab first.');
      setActiveTab('basic');
      return { success: false };
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        photoFile: photoFile,
        oldPhotoPath: null
      };

      const result = await playerProfileService?.create(submitData);
      
      if (result?.error) {
        throw result?.error;
      }

      const newPlayerId = result?.data?.id;
      setCurrentPlayerId(newPlayerId);
      
      return { success: true, playerId: newPlayerId };
    } catch (error) {
      console.error('Error auto-saving player:', error);
      alert('Failed to save player profile: ' + error?.message);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Validation rules
  const validationRules = {
    fullName: {
      required: true,
      minLength: 2,
      maxLength: 100,
      pattern: /^[a-zA-Z\s'-]+$/,
      message: 'Full name is required (2-100 characters, letters only)'
    },
    dateOfBirth: {
      required: true,
      validate: (value) => {
        if (!value) return 'Date of birth is required';
        const date = new Date(value);
        const today = new Date();
        const age = today?.getFullYear() - date?.getFullYear();
        if (age < 13) return 'Player must be at least 13 years old';
        if (age > 50) return 'Please enter a valid date of birth';
        return null;
      }
    },
    nationalityPrimary: {
      required: true,
      minLength: 2,
      message: 'Primary nationality is required'
    },
    height: {
      validate: (value) => {
        if (!value) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 140 || num > 230) {
          return 'Height must be between 140-230 cm';
        }
        return null;
      }
    },
    weight: {
      validate: (value) => {
        if (!value) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 40 || num > 150) {
          return 'Weight must be between 40-150 kg';
        }
        return null;
      }
    },
    agentEmail: {
      validate: (value) => {
        if (!value) return null;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern?.test(value)) {
          return 'Please enter a valid email address';
        }
        return null;
      }
    },
    agentPhone: {
      validate: (value) => {
        if (!value) return null;
        const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
        if (!phonePattern?.test(value)) {
          return 'Please enter a valid phone number';
        }
        return null;
      }
    },
    currentMarketValue: {
      validate: (value) => {
        if (!value) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 0) {
          return 'Market value must be a positive number';
        }
        return null;
      }
    },
    highestMarketValue: {
      validate: (value) => {
        if (!value) return null;
        const num = parseFloat(value);
        if (isNaN(num) || num < 0) {
          return 'Market value must be a positive number';
        }
        return null;
      }
    },
    squadNumber: {
      validate: (value) => {
        if (!value) return null;
        const num = parseInt(value);
        if (isNaN(num) || num < 1 || num > 99) {
          return 'Squad number must be between 1-99';
        }
        return null;
      }
    },
    contractStartDate: {
      validate: (value, formData) => {
        if (!value) return null;
        if (formData?.contractEndDate && new Date(value) >= new Date(formData.contractEndDate)) {
          return 'Start date must be before end date';
        }
        return null;
      }
    },
    contractEndDate: {
      validate: (value, formData) => {
        if (!value) return null;
        if (formData?.contractStartDate && new Date(value) <= new Date(formData.contractStartDate)) {
          return 'End date must be after start date';
        }
        return null;
      }
    }
  };

  // Validate single field
  const validateField = (name, value) => {
    const rule = validationRules?.[name];
    if (!rule) return null;

    // Required validation
    if (rule?.required && (!value || value?.toString()?.trim() === '')) {
      return rule?.message || `${name} is required`;
    }

    // Skip other validations if field is empty and not required
    if (!value || value?.toString()?.trim() === '') {
      return null;
    }

    // Min length validation
    if (rule?.minLength && value?.toString()?.length < rule?.minLength) {
      return rule?.message || `Minimum ${rule?.minLength} characters required`;
    }

    // Max length validation
    if (rule?.maxLength && value?.toString()?.length > rule?.maxLength) {
      return rule?.message || `Maximum ${rule?.maxLength} characters allowed`;
    }

    // Pattern validation
    if (rule?.pattern && !rule?.pattern?.test(value)) {
      return rule?.message || 'Invalid format';
    }

    // Custom validation function
    if (rule?.validate) {
      return rule?.validate(value, formData);
    }

    return null;
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors = {};
    Object.keys(validationRules)?.forEach(fieldName => {
      const error = validateField(fieldName, formData?.[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });
    setErrors(newErrors);
    return { isValid: Object.keys(newErrors)?.length === 0, errors: newErrors };
  };

  // Check if form has required fields filled
  const hasRequiredFields = () => {
    return formData?.fullName && formData?.dateOfBirth && formData?.nationalityPrimary;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));

    // Validate field in real-time
    const error = validateField(name, newValue);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors?.[name];
      }
      return newErrors;
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));

    // Validate field in real-time
    const error = validateField(name, value);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors?.[name];
      }
      return newErrors;
    });
  };

  const handlePhotoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5242880) {
        alert('File size must be less than 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(file?.type)) {
        alert('Only JPEG, PNG, and WebP images are allowed');
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, profilePhoto: '' }));
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData?.[fieldName]);
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[fieldName] = error;
      } else {
        delete newErrors?.[fieldName];
      }
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(validationRules)?.forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    // Validate form
    const validation = validateForm();
    if (!validation?.isValid) {
      alert('Please fix all validation errors before saving.');
      // Scroll to first error using the returned errors object
      const firstErrorField = Object.keys(validation?.errors)?.[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        photoFile: photoFile,
        oldPhotoPath: player?.profilePhoto
      };

      if (player?.id) {
        await playerProfileService?.update(player?.id, submitData);
      } else {
        await playerProfileService?.create(submitData);
      }
      onSave();
    } catch (error) {
      console.error('Error saving player:', error);
      alert('Failed to save player: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: 'User' },
    { id: 'club', label: 'Club Career', icon: 'Building' },
    { id: 'market', label: 'Market Data', icon: 'TrendingUp' },
    { id: 'agent', label: 'Agent Info', icon: 'Briefcase' },
    { id: 'status', label: 'Status', icon: 'Settings' },
    { id: 'career-history', label: 'Career History', icon: 'Calendar' },
    { id: 'performance', label: 'Performance Stats', icon: 'BarChart3' },
    { id: 'advanced-performance', label: 'Advanced Performance', icon: 'Activity' },
    { id: 'competitions', label: 'Competitions', icon: 'Trophy' },
    { id: 'transfers', label: 'Transfer History', icon: 'ArrowRightLeft' },
    { id: 'national-team', label: 'National Team', icon: 'Flag' },
    { id: 'medical', label: 'Medical Records', icon: 'Heart' },
    { id: 'market-value', label: 'Market Value History', icon: 'LineChart' },
    { id: 'media', label: 'Media & Documents', icon: 'FileVideo' }
  ];

  const positionOptions = [
    { value: 'goalkeeper', label: 'Goalkeeper' },
    { value: 'right-back', label: 'Right Back' },
    { value: 'left-back', label: 'Left Back' },
    { value: 'center-back', label: 'Center Back' },
    { value: 'defensive-midfielder', label: 'Defensive Midfielder' },
    { value: 'central-midfielder', label: 'Central Midfielder' },
    { value: 'attacking-midfielder', label: 'Attacking Midfielder' },
    { value: 'right-winger', label: 'Right Winger' },
    { value: 'left-winger', label: 'Left Winger' },
    { value: 'striker', label: 'Striker' },
    { value: 'center-forward', label: 'Center Forward' }
  ];

  const footOptions = [
    { value: 'right', label: 'Right' },
    { value: 'left', label: 'Left' },
    { value: 'both', label: 'Both' }
  ];

  const contractTypeOptions = [
    { value: 'permanent', label: 'Permanent' },
    { value: 'loan', label: 'Loan' },
    { value: 'free-agent', label: 'Free Agent' },
    { value: 'trial', label: 'Trial' }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Public' },
    { value: 'private', label: 'Private' },
    { value: 'verified-only', label: 'Verified Only' }
  ];

  const competitionTypeOptions = [
    { value: 'domestic-league', label: 'Domestic League' },
    { value: 'domestic-cup', label: 'Domestic Cup' },
    { value: 'continental', label: 'Continental' },
    { value: 'international-club', label: 'International Club' },
    { value: 'friendly', label: 'Friendly' }
  ];

  const transferTypeOptions = [
    { value: 'permanent', label: 'Permanent' },
    { value: 'loan', label: 'Loan' },
    { value: 'free-transfer', label: 'Free Transfer' },
    { value: 'end-of-loan', label: 'End of Loan' }
  ];

  const youthLevelOptions = [
    { value: 'u15', label: 'U15' },
    { value: 'u17', label: 'U17' },
    { value: 'u20', label: 'U20' },
    { value: 'u23', label: 'U23' }
  ];

  const injuryTypeOptions = [
    { value: 'muscle', label: 'Muscle' },
    { value: 'ligament', label: 'Ligament' },
    { value: 'bone', label: 'Bone' },
    { value: 'concussion', label: 'Concussion' },
    { value: 'illness', label: 'Illness' },
    { value: 'other', label: 'Other' }
  ];

  const fitnessStatusOptions = [
    { value: 'fit', label: 'Fit' },
    { value: 'recovering', label: 'Recovering' },
    { value: 'injured', label: 'Injured' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const mediaTypeOptions = [
    { value: 'video', label: 'Video' },
    { value: 'image', label: 'Image' },
    { value: 'document', label: 'Document' }
  ];

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Profile Photo
        </label>
        <div className="flex items-start gap-4">
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Player profile preview"
                className="w-32 h-32 object-cover rounded-lg border-2 border-border"
              />
              <button
                type="button"
                onClick={handleRemovePhoto}
                className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1 hover:bg-destructive/90 transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          ) : (
            <div className="w-32 h-32 bg-muted rounded-lg border-2 border-dashed border-border flex items-center justify-center">
              <Icon name="User" size={48} className="text-muted-foreground" />
            </div>
          )}
          <div className="flex-1">
            <input
              type="file"
              id="photo-upload"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoChange}
              className="hidden"
            />
            <label
              htmlFor="photo-upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90 transition-colors"
            >
              <Icon name="Upload" size={16} />
              <span className="text-sm font-medium">Upload Photo</span>
            </label>
            <p className="text-xs text-muted-foreground mt-2">
              Recommended: Square image, at least 400x400px
            </p>
            <p className="text-xs text-muted-foreground">
              Max file size: 5MB. Formats: JPEG, PNG, WebP
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Full Name *"
            name="fullName"
            value={formData?.fullName}
            onChange={handleChange}
            onBlur={() => handleBlur('fullName')}
            required
            className={touched?.fullName && errors?.fullName ? 'border-red-500' : ''}
          />
          {touched?.fullName && errors?.fullName && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors?.fullName}
            </p>
          )}
        </div>
        <Input
          label="Known As / Nickname"
          name="knownAs"
          value={formData?.knownAs}
          onChange={handleChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Date of Birth *"
            name="dateOfBirth"
            type="date"
            value={formData?.dateOfBirth}
            onChange={handleChange}
            onBlur={() => handleBlur('dateOfBirth')}
            required
            className={touched?.dateOfBirth && errors?.dateOfBirth ? 'border-red-500' : ''}
          />
          {touched?.dateOfBirth && errors?.dateOfBirth && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors?.dateOfBirth}
            </p>
          )}
        </div>
        <Input
          label="Place of Birth"
          name="placeOfBirth"
          value={formData?.placeOfBirth}
          onChange={handleChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Input
            label="Primary Nationality *"
            name="nationalityPrimary"
            value={formData?.nationalityPrimary}
            onChange={handleChange}
            onBlur={() => handleBlur('nationalityPrimary')}
            required
            className={touched?.nationalityPrimary && errors?.nationalityPrimary ? 'border-red-500' : ''}
          />
          {touched?.nationalityPrimary && errors?.nationalityPrimary && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors?.nationalityPrimary}
            </p>
          )}
        </div>
        <Input
          label="Secondary Nationality"
          name="nationalitySecondary"
          value={formData?.nationalitySecondary}
          onChange={handleChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            label="Height (cm)"
            name="height"
            type="number"
            step="0.01"
            value={formData?.height}
            onChange={handleChange}
            onBlur={() => handleBlur('height')}
            className={touched?.height && errors?.height ? 'border-red-500' : ''}
          />
          {touched?.height && errors?.height && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors?.height}
            </p>
          )}
        </div>
        <div>
          <Input
            label="Weight (kg)"
            name="weight"
            type="number"
            step="0.01"
            value={formData?.weight}
            onChange={handleChange}
            onBlur={() => handleBlur('weight')}
            className={touched?.weight && errors?.weight ? 'border-red-500' : ''}
          />
          {touched?.weight && errors?.weight && (
            <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {errors?.weight}
            </p>
          )}
        </div>
        <Select
          label="Preferred Foot *"
          value={formData?.preferredFoot}
          onChange={(value) => handleSelectChange('preferredFoot', value)}
          options={footOptions}
        />
      </div>
      <Select
        label="Primary Position *"
        value={formData?.primaryPosition}
        onChange={(value) => handleSelectChange('primaryPosition', value)}
        options={positionOptions}
      />
    </div>
  );

  const renderClubCareer = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Current Club"
          name="currentClub"
          value={formData?.currentClub}
          onChange={handleChange}
        />
        <Input
          label="Current League"
          name="currentLeague"
          value={formData?.currentLeague}
          onChange={handleChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Current Country"
          name="currentCountry"
          value={formData?.currentCountry}
          onChange={handleChange}
        />
        <Input
          label="Squad Number"
          name="squadNumber"
          type="number"
          value={formData?.squadNumber}
          onChange={handleChange}
        />
      </div>
      <Select
        label="Contract Type"
        value={formData?.contractType}
        onChange={(value) => handleSelectChange('contractType', value)}
        options={contractTypeOptions}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Contract Start Date"
          name="contractStartDate"
          type="date"
          value={formData?.contractStartDate}
          onChange={handleChange}
        />
        <Input
          label="Contract End Date"
          name="contractEndDate"
          type="date"
          value={formData?.contractEndDate}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderMarketData = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Current Market Value (€M)"
          name="currentMarketValue"
          type="number"
          step="0.1"
          value={formData?.currentMarketValue}
          onChange={handleChange}
        />
        <Input
          label="Highest Market Value (€M)"
          name="highestMarketValue"
          type="number"
          step="0.1"
          value={formData?.highestMarketValue}
          onChange={handleChange}
        />
      </div>
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={formData?.availableForTransfer}
            onCheckedChange={(checked) => handleSelectChange('availableForTransfer', checked)}
          />
          <span className="text-sm font-medium text-foreground">Available for Transfer</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={formData?.availableForLoan}
            onCheckedChange={(checked) => handleSelectChange('availableForLoan', checked)}
          />
          <span className="text-sm font-medium text-foreground">Available for Loan</span>
        </label>
      </div>
    </div>
  );

  const renderAgentInfo = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Agent Name"
          name="agentName"
          value={formData?.agentName}
          onChange={handleChange}
        />
        <Input
          label="Agency Name"
          name="agencyName"
          value={formData?.agencyName}
          onChange={handleChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Agent Email"
          name="agentEmail"
          type="email"
          value={formData?.agentEmail}
          onChange={handleChange}
        />
        <Input
          label="Agent Phone"
          name="agentPhone"
          value={formData?.agentPhone}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const renderStatus = () => (
    <div className="space-y-4">
      <Select
        label="Profile Visibility"
        value={formData?.profileVisibility}
        onChange={(value) => handleSelectChange('profileVisibility', value)}
        options={visibilityOptions}
      />
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={formData?.verifiedStatus}
            onCheckedChange={(checked) => handleSelectChange('verifiedStatus', checked)}
          />
          <span className="text-sm font-medium text-foreground">Verified Profile</span>
        </label>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={formData?.fifaVerified}
            onCheckedChange={(checked) => handleSelectChange('fifaVerified', checked)}
          />
          <span className="text-sm font-medium text-foreground">FIFA Verified</span>
        </label>
      </div>
    </div>
  );

  const renderCareerHistory = () => (
    <CareerHistoryManagerLocal 
      playerId={currentPlayerId}
      careerHistory={careerHistory}
      onUpdate={loadPlayerData}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderPerformanceStats = () => (
    <PerformanceStatsManagerLocal 
      playerId={currentPlayerId}
      performanceStats={performanceStats}
      onUpdate={loadPlayerData}
      competitionTypeOptions={competitionTypeOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderTransferHistory = () => (
    <TransferHistoryManagerLocal 
      playerId={currentPlayerId}
      transferHistory={transferHistory}
      onUpdate={loadPlayerData}
      transferTypeOptions={transferTypeOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderNationalTeam = () => (
    <NationalTeamManagerLocal 
      playerId={currentPlayerId}
      nationalTeamSenior={nationalTeamSenior}
      nationalTeamYouth={nationalTeamYouth}
      onUpdate={loadPlayerData}
      youthLevelOptions={youthLevelOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderAdvancedPerformance = () => (
    <AdvancedPerformanceManagerLocal 
      playerId={currentPlayerId}
      advancedPerformance={advancedPerformance}
      onUpdate={loadPlayerData}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderCompetitions = () => (
    <CompetitionsManagerLocal 
      playerId={currentPlayerId}
      competitions={competitions}
      onUpdate={loadPlayerData}
      competitionTypeOptions={competitionTypeOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderMedical = () => (
    <MedicalRecordsManagerLocal 
      playerId={currentPlayerId}
      medicalRecords={medicalRecords}
      onUpdate={loadPlayerData}
      injuryTypeOptions={injuryTypeOptions}
      fitnessStatusOptions={fitnessStatusOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderMarketValueHistory = () => (
    <MarketValueHistoryManagerLocal 
      playerId={currentPlayerId}
      marketValueHistory={marketValueHistory}
      onUpdate={loadPlayerData}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  const renderMedia = () => (
    <MediaManagerLocal 
      playerId={currentPlayerId}
      media={media}
      onUpdate={loadPlayerData}
      mediaTypeOptions={mediaTypeOptions}
      autoSavePlayerProfile={autoSavePlayerProfile}
    />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-foreground font-headline">
            {player ? 'Edit Player Profile' : 'Create New Player'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit}>
              {activeTab === 'basic' && renderBasicInfo()}
              {activeTab === 'club' && renderClubCareer()}
              {activeTab === 'market' && renderMarketData()}
              {activeTab === 'agent' && renderAgentInfo()}
              {activeTab === 'status' && renderStatus()}
              {activeTab === 'career-history' && renderCareerHistory()}
              {activeTab === 'performance' && renderPerformanceStats()}
              {activeTab === 'advanced-performance' && renderAdvancedPerformance()}
              {activeTab === 'competitions' && renderCompetitions()}
              {activeTab === 'transfers' && renderTransferHistory()}
              {activeTab === 'national-team' && renderNationalTeam()}
              {activeTab === 'medical' && renderMedical()}
              {activeTab === 'market-value' && renderMarketValueHistory()}
              {activeTab === 'media' && renderMedia()}
            </form>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : player ? 'Update Player' : 'Create Player'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Career History Manager Component
const CareerHistoryManagerLocal = ({ playerId, careerHistory, onUpdate, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    season: '',
    clubName: '',
    league: '',
    country: '',
    squadNumber: '',
    contractType: 'permanent'
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      season: '',
      clubName: '',
      league: '',
      country: '',
      squadNumber: '',
      contractType: 'permanent'
    });
  };

  const handleEdit = (career) => {
    setEditingId(career?.id);
    setFormData({
      season: career?.season || '',
      clubName: career?.clubName || '',
      league: career?.league || '',
      country: career?.country || '',
      squadNumber: career?.squadNumber || '',
      contractType: career?.contractType || 'permanent'
    });
  };

  const handleSaveAndClose = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      // Use the newly created player ID
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        await clubCareerService?.create(data);
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving career history:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await clubCareerService?.update(editingId, data);
      } else {
        await clubCareerService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving career history:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      // Use the newly created player ID
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await clubCareerService?.update(editingId, data);
          setEditingId(null);
        } else {
          await clubCareerService?.create(data);
        }
        // Reset form but keep it open
        setFormData({
          season: '',
          clubName: '',
          league: '',
          country: '',
          squadNumber: '',
          contractType: 'permanent'
        });
        onUpdate();
      } catch (error) {
        console.error('Error saving career history:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await clubCareerService?.update(editingId, data);
        setEditingId(null);
      } else {
        await clubCareerService?.create(data);
      }
      // Reset form but keep it open
      setFormData({
        season: '',
        clubName: '',
        league: '',
        country: '',
        squadNumber: '',
        contractType: 'permanent'
      });
      onUpdate();
    } catch (error) {
      console.error('Error saving career history:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this career record?')) return;
    try {
      await clubCareerService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting career history:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Season-by-Season Career History</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Season
          </Button>
        )}
      </div>

      {!playerId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> When you click save below, the player profile will be automatically created first, then your career history will be saved.
          </p>
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Season *"
              placeholder="e.g., 2023/24"
              value={formData?.season}
              onChange={(e) => setFormData({ ...formData, season: e?.target?.value })}
            />
            <Input
              label="Club Name *"
              value={formData?.clubName}
              onChange={(e) => setFormData({ ...formData, clubName: e?.target?.value })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="League *"
              value={formData?.league}
              onChange={(e) => setFormData({ ...formData, league: e?.target?.value })}
            />
            <Input
              label="Country *"
              value={formData?.country}
              onChange={(e) => setFormData({ ...formData, country: e?.target?.value })}
            />
            <Input
              label="Squad Number"
              type="number"
              value={formData?.squadNumber}
              onChange={(e) => setFormData({ ...formData, squadNumber: e?.target?.value })}
            />
          </div>
          <Select
            label="Contract Type"
            value={formData?.contractType}
            onChange={(value) => setFormData({ ...formData, contractType: value })}
            options={[
              { value: 'permanent', label: 'Permanent' },
              { value: 'loan', label: 'Loan' },
              { value: 'free-agent', label: 'Free Agent' },
              { value: 'trial', label: 'Trial' }
            ]}
          />
          <div className="flex gap-2">
            {editingId ? (
              <Button size="sm" onClick={handleSaveAndClose}>
                Update
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleSaveAndAddAnother} iconName="Plus" iconPosition="left">
                  Save & Add Another
                </Button>
                <Button size="sm" variant="outline" onClick={handleSaveAndClose}>
                  Save & Close
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {careerHistory?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No career history records yet</p>
        ) : (
          careerHistory?.map((career) => (
            <div key={career?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{career?.season} - {career?.clubName}</p>
                <p className="text-sm text-muted-foreground">{career?.league} • {career?.country} • #{career?.squadNumber || '-'}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(career)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(career?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Performance Stats Manager Component
const PerformanceStatsManagerLocal = ({ playerId, performanceStats, onUpdate, competitionTypeOptions, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    season: '',
    competitionType: 'domestic-league',
    appearances: 0,
    gamesStarted: 0,
    minutesPlayed: 0,
    goalsScored: 0,
    assists: 0,
    yellowCards: 0,
    redCards: 0
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      season: '',
      competitionType: 'domestic-league',
      appearances: 0,
      gamesStarted: 0,
      minutesPlayed: 0,
      goalsScored: 0,
      assists: 0,
      yellowCards: 0,
      redCards: 0
    });
  };

  const handleEdit = (stats) => {
    setEditingId(stats?.id);
    setFormData({
      season: stats?.season || '',
      competitionType: stats?.competitionType || 'domestic-league',
      appearances: stats?.appearances || 0,
      gamesStarted: stats?.gamesStarted || 0,
      minutesPlayed: stats?.minutesPlayed || 0,
      goalsScored: stats?.goalsScored || 0,
      assists: stats?.assists || 0,
      yellowCards: stats?.yellowCards || 0,
      redCards: stats?.redCards || 0
    });
  };

  const handleSaveAndClose = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await performanceStatsService?.update(editingId, data);
        } else {
          await performanceStatsService?.create(data);
        }
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving performance stats:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await performanceStatsService?.update(editingId, data);
      } else {
        await performanceStatsService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving performance stats:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await performanceStatsService?.update(editingId, data);
          setEditingId(null);
        } else {
          await performanceStatsService?.create(data);
        }
        // Reset form but keep it open
        setFormData({
          season: '',
          competitionType: 'domestic-league',
          appearances: 0,
          gamesStarted: 0,
          minutesPlayed: 0,
          goalsScored: 0,
          assists: 0,
          yellowCards: 0,
          redCards: 0
        });
        onUpdate();
      } catch (error) {
        console.error('Error saving performance stats:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await performanceStatsService?.update(editingId, data);
        setEditingId(null);
      } else {
        await performanceStatsService?.create(data);
      }
      // Reset form but keep it open
      setFormData({
        season: '',
        competitionType: 'domestic-league',
        appearances: 0,
        gamesStarted: 0,
        minutesPlayed: 0,
        goalsScored: 0,
        assists: 0,
        yellowCards: 0,
        redCards: 0
      });
      onUpdate();
    } catch (error) {
      console.error('Error saving performance stats:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this performance record?')) return;
    try {
      await performanceStatsService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting performance stats:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Performance Statistics</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Stats
          </Button>
        )}
      </div>

      {!playerId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> Save the player's basic information first, then you can add unlimited performance statistics.
          </p>
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Season *"
              placeholder="e.g., 2023/24"
              value={formData?.season}
              onChange={(e) => setFormData({ ...formData, season: e?.target?.value })}
            />
            <Select
              label="Competition Type *"
              value={formData?.competitionType}
              onChange={(value) => setFormData({ ...formData, competitionType: value })}
              options={competitionTypeOptions}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              label="Appearances"
              type="number"
              value={formData?.appearances}
              onChange={(e) => setFormData({ ...formData, appearances: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Started"
              type="number"
              value={formData?.gamesStarted}
              onChange={(e) => setFormData({ ...formData, gamesStarted: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Minutes"
              type="number"
              value={formData?.minutesPlayed}
              onChange={(e) => setFormData({ ...formData, minutesPlayed: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Goals"
              type="number"
              value={formData?.goalsScored}
              onChange={(e) => setFormData({ ...formData, goalsScored: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Input
              label="Assists"
              type="number"
              value={formData?.assists}
              onChange={(e) => setFormData({ ...formData, assists: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Yellow Cards"
              type="number"
              value={formData?.yellowCards}
              onChange={(e) => setFormData({ ...formData, yellowCards: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Red Cards"
              type="number"
              value={formData?.redCards}
              onChange={(e) => setFormData({ ...formData, redCards: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="flex gap-2">
            {editingId ? (
              <Button size="sm" onClick={handleSaveAndClose}>
                Update
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleSaveAndAddAnother} iconName="Plus" iconPosition="left">
                  Save & Add Another
                </Button>
                <Button size="sm" variant="outline" onClick={handleSaveAndClose}>
                  Save & Close
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {performanceStats?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No performance statistics yet</p>
        ) : (
          performanceStats?.map((stats) => (
            <div key={stats?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{stats?.season} - {stats?.competitionType?.replace('-', ' ')}</p>
                <p className="text-sm text-muted-foreground">
                  {stats?.appearances} Apps • {stats?.goalsScored} Goals • {stats?.assists} Assists • {stats?.minutesPlayed} Mins
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(stats)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(stats?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Transfer History Manager Component
const TransferHistoryManagerLocal = ({ playerId, transferHistory, onUpdate, transferTypeOptions, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    fromClub: '',
    toClub: '',
    transferDate: '',
    transferFee: '',
    transferType: 'permanent'
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      fromClub: '',
      toClub: '',
      transferDate: '',
      transferFee: '',
      transferType: 'permanent'
    });
  };

  const handleEdit = (transfer) => {
    setEditingId(transfer?.id);
    setFormData({
      fromClub: transfer?.fromClub || '',
      toClub: transfer?.toClub || '',
      transferDate: transfer?.transferDate || '',
      transferFee: transfer?.transferFee || '',
      transferType: transfer?.transferType || 'permanent'
    });
  };

  const handleSaveAndClose = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await transferHistoryService?.update(editingId, data);
        } else {
          await transferHistoryService?.create(data);
        }
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving transfer history:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await transferHistoryService?.update(editingId, data);
      } else {
        await transferHistoryService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving transfer history:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await transferHistoryService?.update(editingId, data);
          setEditingId(null);
        } else {
          await transferHistoryService?.create(data);
        }
        // Reset form but keep it open
        setFormData({
          fromClub: '',
          toClub: '',
          transferDate: '',
          transferFee: '',
          transferType: 'permanent'
        });
        onUpdate();
      } catch (error) {
        console.error('Error saving transfer history:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await transferHistoryService?.update(editingId, data);
        setEditingId(null);
      } else {
        await transferHistoryService?.create(data);
      }
      // Reset form but keep it open
      setFormData({
        fromClub: '',
        toClub: '',
        transferDate: '',
        transferFee: '',
        transferType: 'permanent'
      });
      onUpdate();
    } catch (error) {
      console.error('Error saving transfer history:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transfer record?')) return;
    try {
      await transferHistoryService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting transfer history:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Transfer History</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Transfer
          </Button>
        )}
      </div>

      {!playerId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> When you click save below, the player profile will be automatically created first, then your transfer history will be saved.
          </p>
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="From Club *"
              value={formData?.fromClub}
              onChange={(e) => setFormData({ ...formData, fromClub: e?.target?.value })}
            />
            <Input
              label="To Club *"
              value={formData?.toClub}
              onChange={(e) => setFormData({ ...formData, toClub: e?.target?.value })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Transfer Date *"
              type="date"
              value={formData?.transferDate}
              onChange={(e) => setFormData({ ...formData, transferDate: e?.target?.value })}
            />
            <Input
              label="Transfer Fee (€M)"
              type="number"
              step="0.1"
              placeholder="Leave empty for free"
              value={formData?.transferFee}
              onChange={(e) => setFormData({ ...formData, transferFee: e?.target?.value })}
            />
            <Select
              label="Transfer Type *"
              value={formData?.transferType}
              onChange={(value) => setFormData({ ...formData, transferType: value })}
              options={transferTypeOptions}
            />
          </div>
          <div className="flex gap-2">
            {editingId ? (
              <Button size="sm" onClick={handleSaveAndClose}>
                Update
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleSaveAndAddAnother} iconName="Plus" iconPosition="left">
                  Save & Add Another
                </Button>
                <Button size="sm" variant="outline" onClick={handleSaveAndClose}>
                  Save & Close
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {transferHistory?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No transfer history yet</p>
        ) : (
          transferHistory?.map((transfer) => (
            <div key={transfer?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{transfer?.fromClub} → {transfer?.toClub}</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(transfer?.transferDate)?.toLocaleDateString()} • 
                  {transfer?.transferFee ? ` €${transfer?.transferFee}M` : ' Free'} • 
                  {transfer?.transferType?.replace('-', ' ')}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(transfer)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(transfer?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// National Team Manager Component
const NationalTeamManagerLocal = ({ playerId, nationalTeamSenior, nationalTeamYouth, onUpdate, youthLevelOptions, autoSavePlayerProfile }) => {
  const [isAddingSenior, setIsAddingSenior] = useState(false);
  const [isAddingYouth, setIsAddingYouth] = useState(false);
  const [seniorData, setSeniorData] = useState({
    debutDate: '',
    totalCaps: 0,
    totalGoals: 0,
    majorTournamentsPlayed: ''
  });
  const [youthData, setYouthData] = useState({
    youthLevel: 'u17',
    yearsActive: '',
    caps: 0,
    goals: 0,
    competitionsPlayed: ''
  });

  const handleSaveSenior = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...seniorData, playerId: newPlayerId };
        await nationalTeamSeniorService?.create(data);
        setIsAddingSenior(false);
        onUpdate();
      } catch (error) {
        console.error('Error saving senior national team data:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...seniorData, playerId };
      await nationalTeamSeniorService?.create(data);
      setIsAddingSenior(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving senior national team data:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveYouth = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...youthData, playerId: newPlayerId };
        await nationalTeamYouthService?.create(data);
        setIsAddingYouth(false);
        onUpdate();
      } catch (error) {
        console.error('Error saving youth national team data:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...youthData, playerId };
      await nationalTeamYouthService?.create(data);
      setIsAddingYouth(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving youth national team data:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Senior National Team</h3>
          {!nationalTeamSenior && !isAddingSenior && (
            <Button size="sm" onClick={() => setIsAddingSenior(true)} iconName="Plus" iconPosition="left">
              Add Senior Data
            </Button>
          )}
        </div>

        {isAddingSenior && (
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Debut Date"
                type="date"
                value={seniorData?.debutDate}
                onChange={(e) => setSeniorData({ ...seniorData, debutDate: e?.target?.value })}
              />
              <Input
                label="Total Caps"
                type="number"
                value={seniorData?.totalCaps}
                onChange={(e) => setSeniorData({ ...seniorData, totalCaps: parseInt(e?.target?.value) || 0 })}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Total Goals"
                type="number"
                value={seniorData?.totalGoals}
                onChange={(e) => setSeniorData({ ...seniorData, totalGoals: parseInt(e?.target?.value) || 0 })}
              />
              <Input
                label="Major Tournaments"
                placeholder="e.g., AFCON 2023, World Cup 2022"
                value={seniorData?.majorTournamentsPlayed}
                onChange={(e) => setSeniorData({ ...seniorData, majorTournamentsPlayed: e?.target?.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveSenior}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsAddingSenior(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {nationalTeamSenior && (
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Debut: {new Date(nationalTeamSenior?.debutDate)?.toLocaleDateString()}</p>
            <p className="text-sm text-muted-foreground">
              {nationalTeamSenior?.totalCaps} Caps • {nationalTeamSenior?.totalGoals} Goals
            </p>
            {nationalTeamSenior?.majorTournamentsPlayed && (
              <p className="text-sm text-muted-foreground mt-1">Tournaments: {nationalTeamSenior?.majorTournamentsPlayed}</p>
            )}
          </div>
        )}

        {!nationalTeamSenior && !isAddingSenior && (
          <p className="text-sm text-muted-foreground text-center py-4">No senior national team data</p>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Youth National Team</h3>
          {!isAddingYouth && (
            <Button size="sm" onClick={() => setIsAddingYouth(true)} iconName="Plus" iconPosition="left">
              Add Youth Data
            </Button>
          )}
        </div>

        {isAddingYouth && (
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Select
                label="Youth Level *"
                value={youthData?.youthLevel}
                onChange={(value) => setYouthData({ ...youthData, youthLevel: value })}
                options={youthLevelOptions}
              />
              <Input
                label="Years Active"
                placeholder="e.g., 2018-2020"
                value={youthData?.yearsActive}
                onChange={(e) => setYouthData({ ...youthData, yearsActive: e?.target?.value })}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Caps"
                type="number"
                value={youthData?.caps}
                onChange={(e) => setYouthData({ ...youthData, caps: parseInt(e?.target?.value) || 0 })}
              />
              <Input
                label="Goals"
                type="number"
                value={youthData?.goals}
                onChange={(e) => setYouthData({ ...youthData, goals: parseInt(e?.target?.value) || 0 })}
              />
              <Input
                label="Competitions"
                placeholder="e.g., U17 AFCON"
                value={youthData?.competitionsPlayed}
                onChange={(e) => setYouthData({ ...youthData, competitionsPlayed: e?.target?.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSaveYouth}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsAddingYouth(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {nationalTeamYouth?.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No youth national team data</p>
          ) : (
            nationalTeamYouth?.map((youth) => (
              <div key={youth?.id} className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-foreground">{youth?.youthLevel?.toUpperCase()} - {youth?.yearsActive}</p>
                <p className="text-sm text-muted-foreground">
                  {youth?.caps} Caps • {youth?.goals} Goals • {youth?.competitionsPlayed}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Advanced Performance Manager Component
const AdvancedPerformanceManagerLocal = ({ playerId, advancedPerformance, onUpdate, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    season: '',
    passAccuracyPercent: 0,
    dribblesCompleted: 0,
    duelsWonPercent: 0,
    aerialDuelsWon: 0,
    tacklesMade: 0,
    interceptions: 0,
    distanceCoveredKm: 0,
    sprintCount: 0
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      season: '',
      passAccuracyPercent: 0,
      dribblesCompleted: 0,
      duelsWonPercent: 0,
      aerialDuelsWon: 0,
      tacklesMade: 0,
      interceptions: 0,
      distanceCoveredKm: 0,
      sprintCount: 0
    });
  };

  const handleEdit = (data) => {
    setEditingId(data?.id);
    setFormData({
      season: data?.season || '',
      passAccuracyPercent: data?.passAccuracyPercent || 0,
      dribblesCompleted: data?.dribblesCompleted || 0,
      duelsWonPercent: data?.duelsWonPercent || 0,
      aerialDuelsWon: data?.aerialDuelsWon || 0,
      tacklesMade: data?.tacklesMade || 0,
      interceptions: data?.interceptions || 0,
      distanceCoveredKm: data?.distanceCoveredKm || 0,
      sprintCount: data?.sprintCount || 0
    });
  };

  const handleSaveAndClose = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await advancedPerformanceService?.update(editingId, data);
        } else {
          await advancedPerformanceService?.create(data);
        }
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving advanced performance:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await advancedPerformanceService?.update(editingId, data);
      } else {
        await advancedPerformanceService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving advanced performance:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await advancedPerformanceService?.update(editingId, data);
          setEditingId(null);
        } else {
          await advancedPerformanceService?.create(data);
        }
        // Reset form but keep it open
        setFormData({
          season: '',
          passAccuracyPercent: 0,
          dribblesCompleted: 0,
          duelsWonPercent: 0,
          aerialDuelsWon: 0,
          tacklesMade: 0,
          interceptions: 0,
          distanceCoveredKm: 0,
          sprintCount: 0
        });
        onUpdate();
      } catch (error) {
        console.error('Error saving advanced performance:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await advancedPerformanceService?.update(editingId, data);
        setEditingId(null);
      } else {
        await advancedPerformanceService?.create(data);
      }
      // Reset form but keep it open
      setFormData({
        season: '',
        passAccuracyPercent: 0,
        dribblesCompleted: 0,
        duelsWonPercent: 0,
        aerialDuelsWon: 0,
        tacklesMade: 0,
        interceptions: 0,
        distanceCoveredKm: 0,
        sprintCount: 0
      });
      onUpdate();
    } catch (error) {
      console.error('Error saving advanced performance:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await advancedPerformanceService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Advanced Performance Metrics</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Metrics
          </Button>
        )}
      </div>

      {!playerId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> When you click save below, the player profile will be automatically created first, then your advanced performance metrics will be saved.
          </p>
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <Input
            label="Season *"
            placeholder="e.g., 2023/24"
            value={formData?.season}
            onChange={(e) => setFormData({ ...formData, season: e?.target?.value })}
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              label="Pass Accuracy %"
              type="number"
              step="0.01"
              value={formData?.passAccuracyPercent}
              onChange={(e) => setFormData({ ...formData, passAccuracyPercent: parseFloat(e?.target?.value) || 0 })}
            />
            <Input
              label="Dribbles Completed"
              type="number"
              value={formData?.dribblesCompleted}
              onChange={(e) => setFormData({ ...formData, dribblesCompleted: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Duels Won %"
              type="number"
              step="0.01"
              value={formData?.duelsWonPercent}
              onChange={(e) => setFormData({ ...formData, duelsWonPercent: parseFloat(e?.target?.value) || 0 })}
            />
            <Input
              label="Aerial Duels Won"
              type="number"
              value={formData?.aerialDuelsWon}
              onChange={(e) => setFormData({ ...formData, aerialDuelsWon: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              label="Tackles Made"
              type="number"
              value={formData?.tacklesMade}
              onChange={(e) => setFormData({ ...formData, tacklesMade: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Interceptions"
              type="number"
              value={formData?.interceptions}
              onChange={(e) => setFormData({ ...formData, interceptions: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Distance (km)"
              type="number"
              step="0.01"
              value={formData?.distanceCoveredKm}
              onChange={(e) => setFormData({ ...formData, distanceCoveredKm: parseFloat(e?.target?.value) || 0 })}
            />
            <Input
              label="Sprint Count"
              type="number"
              value={formData?.sprintCount}
              onChange={(e) => setFormData({ ...formData, sprintCount: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="flex gap-2">
            {editingId ? (
              <Button size="sm" onClick={handleSaveAndClose}>
                Update
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleSaveAndAddAnother} iconName="Plus" iconPosition="left">
                  Save & Add Another
                </Button>
                <Button size="sm" variant="outline" onClick={handleSaveAndClose}>
                  Save & Close
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {advancedPerformance?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No advanced performance data yet</p>
        ) : (
          advancedPerformance?.map((data) => (
            <div key={data?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{data?.season}</p>
                <p className="text-sm text-muted-foreground">
                  Pass: {data?.passAccuracyPercent}% • Dribbles: {data?.dribblesCompleted} • Tackles: {data?.tacklesMade}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(data)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(data?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Competitions Manager Component
const CompetitionsManagerLocal = ({ playerId, competitions, onUpdate, competitionTypeOptions, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    season: '',
    competitionName: '',
    competitionType: 'domestic-league',
    appearances: 0,
    goals: 0,
    assists: 0,
    minutesPlayed: 0
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      season: '',
      competitionName: '',
      competitionType: 'domestic-league',
      appearances: 0,
      goals: 0,
      assists: 0,
      minutesPlayed: 0
    });
  };

  const handleEdit = (comp) => {
    setEditingId(comp?.id);
    setFormData({
      season: comp?.season || '',
      competitionName: comp?.competitionName || '',
      competitionType: comp?.competitionType || 'domestic-league',
      appearances: comp?.appearances || 0,
      goals: comp?.goals || 0,
      assists: comp?.assists || 0,
      minutesPlayed: comp?.minutesPlayed || 0
    });
  };

  const handleSaveAndClose = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await competitionsPlayedService?.update(editingId, data);
        } else {
          await competitionsPlayedService?.create(data);
        }
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving competition:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await competitionsPlayedService?.update(editingId, data);
      } else {
        await competitionsPlayedService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving competition:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await competitionsPlayedService?.update(editingId, data);
          setEditingId(null);
        } else {
          await competitionsPlayedService?.create(data);
        }
        // Reset form but keep it open
        setFormData({
          season: '',
          competitionName: '',
          competitionType: 'domestic-league',
          appearances: 0,
          goals: 0,
          assists: 0,
          minutesPlayed: 0
        });
        onUpdate();
      } catch (error) {
        console.error('Error saving competition:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await competitionsPlayedService?.update(editingId, data);
        setEditingId(null);
      } else {
        await competitionsPlayedService?.create(data);
      }
      // Reset form but keep it open
      setFormData({
        season: '',
        competitionName: '',
        competitionType: 'domestic-league',
        appearances: 0,
        goals: 0,
        assists: 0,
        minutesPlayed: 0
      });
      onUpdate();
    } catch (error) {
      console.error('Error saving competition:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this competition record?')) return;
    try {
      await competitionsPlayedService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Competitions Played</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Competition
          </Button>
        )}
      </div>

      {!playerId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> When you click save below, the player profile will be automatically created first, then your competition records will be saved.
          </p>
        </div>
      )}

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Season *"
              placeholder="e.g., 2023/24"
              value={formData?.season}
              onChange={(e) => setFormData({ ...formData, season: e?.target?.value })}
            />
            <Select
              label="Competition Type *"
              value={formData?.competitionType}
              onChange={(value) => setFormData({ ...formData, competitionType: value })}
              options={competitionTypeOptions}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Input
              label="Appearances"
              type="number"
              value={formData?.appearances}
              onChange={(e) => setFormData({ ...formData, appearances: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Goals"
              type="number"
              value={formData?.goals}
              onChange={(e) => setFormData({ ...formData, goals: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Assists"
              type="number"
              value={formData?.assists}
              onChange={(e) => setFormData({ ...formData, assists: parseInt(e?.target?.value) || 0 })}
            />
            <Input
              label="Minutes"
              type="number"
              value={formData?.minutesPlayed}
              onChange={(e) => setFormData({ ...formData, minutesPlayed: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="flex gap-2">
            {editingId ? (
              <Button size="sm" onClick={handleSaveAndClose}>
                Update
              </Button>
            ) : (
              <>
                <Button size="sm" onClick={handleSaveAndAddAnother} iconName="Plus" iconPosition="left">
                  Save & Add Another
                </Button>
                <Button size="sm" variant="outline" onClick={handleSaveAndClose}>
                  Save & Close
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {competitions?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No competition records yet</p>
        ) : (
          competitions?.map((comp) => (
            <div key={comp?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{comp?.competitionName} ({comp?.season})</p>
                <p className="text-sm text-muted-foreground">
                  {comp?.appearances} Apps • {comp?.goals} Goals • {comp?.assists} Assists
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(comp)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(comp?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Medical Records Manager Component
const MedicalRecordsManagerLocal = ({ playerId, medicalRecords, onUpdate, injuryTypeOptions, fitnessStatusOptions, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    injuryType: 'muscle',
    injuryDescription: '',
    injuryStartDate: '',
    injuryEndDate: '',
    matchesMissed: 0,
    fitnessStatus: 'fit'
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      injuryType: 'muscle',
      injuryDescription: '',
      injuryStartDate: '',
      injuryEndDate: '',
      matchesMissed: 0,
      fitnessStatus: 'fit'
    });
  };

  const handleEdit = (record) => {
    setEditingId(record?.id);
    setFormData({
      injuryType: record?.injuryType || 'muscle',
      injuryDescription: record?.injuryDescription || '',
      injuryStartDate: record?.injuryStartDate || '',
      injuryEndDate: record?.injuryEndDate || '',
      matchesMissed: record?.matchesMissed || 0,
      fitnessStatus: record?.fitnessStatus || 'fit'
    });
  };

  const handleSave = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        if (editingId) {
          await medicalRecordsService?.update(editingId, data);
        } else {
          await medicalRecordsService?.create(data);
        }
        setIsAdding(false);
        setEditingId(null);
        onUpdate();
      } catch (error) {
        console.error('Error saving medical record:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      if (editingId) {
        await medicalRecordsService?.update(editingId, data);
      } else {
        await medicalRecordsService?.create(data);
      }
      setIsAdding(false);
      setEditingId(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving medical record:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this medical record?')) return;
    try {
      await medicalRecordsService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Medical & Injury Records</h3>
        {!isAdding && !editingId && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Record
          </Button>
        )}
      </div>

      {(isAdding || editingId) && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Injury Type *"
              value={formData?.injuryType}
              onChange={(value) => setFormData({ ...formData, injuryType: value })}
              options={injuryTypeOptions}
            />
            <Select
              label="Fitness Status *"
              value={formData?.fitnessStatus}
              onChange={(value) => setFormData({ ...formData, fitnessStatus: value })}
              options={fitnessStatusOptions}
            />
          </div>
          <Input
            label="Injury Description"
            value={formData?.injuryDescription}
            onChange={(e) => setFormData({ ...formData, injuryDescription: e?.target?.value })}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Start Date *"
              type="date"
              value={formData?.injuryStartDate}
              onChange={(e) => setFormData({ ...formData, injuryStartDate: e?.target?.value })}
            />
            <Input
              label="End Date"
              type="date"
              value={formData?.injuryEndDate}
              onChange={(e) => setFormData({ ...formData, injuryEndDate: e?.target?.value })}
            />
            <Input
              label="Matches Missed"
              type="number"
              value={formData?.matchesMissed}
              onChange={(e) => setFormData({ ...formData, matchesMissed: parseInt(e?.target?.value) || 0 })}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>
              {editingId ? 'Update' : 'Save'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {medicalRecords?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No medical records yet</p>
        ) : (
          medicalRecords?.map((record) => (
            <div key={record?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground capitalize">{record?.injuryType} Injury</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(record?.injuryStartDate)?.toLocaleDateString()} - 
                  {record?.injuryEndDate ? new Date(record?.injuryEndDate)?.toLocaleDateString() : 'Ongoing'} • 
                  {record?.matchesMissed} matches missed
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(record)}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                >
                  <Icon name="Edit" size={16} />
                </button>
                <button
                  onClick={() => handleDelete(record?.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Market Value History Manager Component
const MarketValueHistoryManagerLocal = ({ playerId, marketValueHistory, onUpdate, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    valuationDate: '',
    marketValue: 0
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      valuationDate: '',
      marketValue: 0
    });
  };

  const handleSave = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        await marketValueHistoryService?.create(data);
        setIsAdding(false);
        onUpdate();
      } catch (error) {
        console.error('Error saving market value:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      await marketValueHistoryService?.create(data);
      setIsAdding(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving market value:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this valuation?')) return;
    try {
      await marketValueHistoryService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Market Value History</h3>
        {!isAdding && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Valuation
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Valuation Date *"
              type="date"
              value={formData?.valuationDate}
              onChange={(e) => setFormData({ ...formData, valuationDate: e?.target?.value })}
            />
            <Input
              label="Market Value (€M) *"
              type="number"
              step="0.1"
              value={formData?.marketValue}
              onChange={(e) => setFormData({ ...formData, marketValue: parseFloat(e?.target?.value) || 0 })}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {marketValueHistory?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No market value history yet</p>
        ) : (
          marketValueHistory?.map((value) => (
            <div key={value?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">€{value?.marketValue}M</p>
                <p className="text-sm text-muted-foreground">{new Date(value?.valuationDate)?.toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => handleDelete(value?.id)}
                className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Media Manager Component
const MediaManagerLocal = ({ playerId, media, onUpdate, mediaTypeOptions, autoSavePlayerProfile }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    mediaType: 'video',
    title: '',
    description: '',
    filePath: ''
  });

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      mediaType: 'video',
      title: '',
      description: '',
      filePath: ''
    });
  };

  const handleSave = async () => {
    if (!playerId) {
      const result = await autoSavePlayerProfile();
      if (!result?.success) return;
      
      const newPlayerId = result?.playerId;
      
      try {
        const data = { ...formData, playerId: newPlayerId };
        await playerMediaService?.create(data);
        setIsAdding(false);
        onUpdate();
      } catch (error) {
        console.error('Error saving media:', error);
        alert('Failed to save: ' + error?.message);
      }
      return;
    }

    try {
      const data = { ...formData, playerId };
      await playerMediaService?.create(data);
      setIsAdding(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving media:', error);
      alert('Failed to save: ' + error?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media file?')) return;
    try {
      await playerMediaService?.delete(id);
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete: ' + error?.message);
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Media & Documents</h3>
        {!isAdding && (
          <Button size="sm" onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add Media
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="bg-muted p-4 rounded-lg space-y-3">
          <Select
            label="Media Type *"
            value={formData?.mediaType}
            onChange={(value) => setFormData({ ...formData, mediaType: value })}
            options={mediaTypeOptions}
          />
          <Input
            label="Title *"
            value={formData?.title}
            onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
          />
          <Input
            label="Description"
            value={formData?.description}
            onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
          />
          <Input
            label="File URL *"
            placeholder="https://..."
            value={formData?.filePath}
            onChange={(e) => setFormData({ ...formData, filePath: e?.target?.value })}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {media?.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No media files yet</p>
        ) : (
          media?.map((item) => (
            <div key={item?.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="font-medium text-foreground">{item?.title}</p>
                <p className="text-sm text-muted-foreground capitalize">{item?.mediaType}</p>
              </div>
              <button
                onClick={() => handleDelete(item?.id)}
                className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
              >
                <Icon name="Trash2" size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PlayerEditModal;